import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import {
  Card,
  CardHeader,
  Button,
  CardContent,
  List,
  Divider,
  ListItem,
  ListItemAvatar,
  IconButton,
} from "@mui/material";
import { Add, EditOutlined, DeleteForeverOutlined } from "@mui/icons-material";

import { SkillTemplate } from "../../../../src/API";
import ExternalVideoSelector from "../../external-video-selector";
import { Vid } from "../../../../types/skills";
import { useSkillTemplates } from "../../../../providers/state/skillTemplate";
import ExternalVideoEmbed from "../../../media/external-video-embed";
import ConfirmDialog from "../../confirmation";

const initSelection: Vid = {
  title: "",
  when: 0,
  url: "",
  description: "",
};

const ExternalVideos = ({
  template,
  refresh,
  editable,
}: {
  template: Partial<SkillTemplate>;
  refresh: () => Promise<void>;
  editable: boolean;
}) => {
  const [externalVideos, setExternalVideos] = useState<Vid[]>([]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [editVid, setEditVid] = useState<Vid>(initSelection);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteAction = useRef<() => void>();

  const { upsertSkillTemplate } = useSkillTemplates();

  useEffect(() => {
    setExternalVideos(JSON.parse(template.externalVideos || "[]"));
  }, [template]);

  const openAddVid = useCallback(() => {
    setEditVid({ ...initSelection, when: new Date().valueOf() });
    setSelectorOpen(true);
  }, []);

  const openEditVid = useCallback((vid: Vid) => {
    setEditVid(vid);
    setSelectorOpen(true);
  }, []);

  const upsertVid = useCallback(async () => {
    const match = externalVideos.find((v) => v.when === editVid.when);
    let result;
    if (match) {
      match.title = editVid.title;
      match.url = editVid.url;
      match.description = editVid.description;
      result = await upsertSkillTemplate({
        id: template.id,
        externalVideos: JSON.stringify(externalVideos),
      });
    } else {
      result = await upsertSkillTemplate({
        id: template.id,
        externalVideos: JSON.stringify([...externalVideos, editVid]),
      });
    }

    await refresh();
    setSelectorOpen(false);
    return result;
  }, [template, externalVideos, editVid, upsertSkillTemplate, refresh]);

  const deleteVid = useCallback(
    async (vid: any) => {
      const result = await upsertSkillTemplate({
        id: template.id,
        externalVideos: JSON.stringify(externalVideos.filter((v) => v !== vid)),
      });

      await refresh();
      return result;
    },
    [upsertSkillTemplate, template, externalVideos, refresh]
  );

  const handleDelete = useCallback(() => {
    if (deleteAction.current) deleteAction.current();
    deleteAction.current = undefined;
  }, []);

  return (
    <>
      <Card>
        <CardHeader
          action={
            editable ? (
              <Button
                endIcon={<Add />}
                color="success"
                size="small"
                variant="contained"
                onClick={openAddVid}
              >
                Add
              </Button>
            ) : null
          }
          title="EXTERNAL VIDEOS (YouTube)"
          sx={{ "& span": { fontSize: "1rem" } }}
        />
        <CardContent>
          <List
            sx={{
              width: "100%",
              maxWidth: "34rem",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {externalVideos.map((vid) => (
              <Fragment key={vid.when}>
                <Divider />
                <ListItem sx={{ alignItems: "flex-start" }}>
                  <ListItemAvatar
                    sx={{
                      marginRight: "1rem",
                      minWidth: 128,
                      width: "50%",
                      maxWidth: 384,
                    }}
                  >
                    <ExternalVideoEmbed url={vid.url} />
                  </ListItemAvatar>
                  <div className="flex-auto relative">
                    {editable ? (
                      <div className="float-right">
                        <IconButton
                          onClick={() => {
                            openEditVid(vid);
                          }}
                          aria-label="edit"
                          size="small"
                        >
                          <EditOutlined />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            deleteAction.current = () => deleteVid(vid);
                            setDeleteOpen(true);
                          }}
                          aria-label="delete"
                          size="small"
                        >
                          <DeleteForeverOutlined />
                        </IconButton>
                      </div>
                    ) : null}
                    <h4 className="font-bold">{vid.title}</h4>
                    <p className="font-bold text-sm mb-2">
                      {new Date(vid.when).toLocaleDateString()}{" "}
                      {new Date(vid.when).toLocaleTimeString()}
                    </p>
                    <p className="leading-tight">{vid.description}</p>
                  </div>
                </ListItem>
              </Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
      <ExternalVideoSelector
        open={selectorOpen}
        setOpen={setSelectorOpen}
        extVid={editVid}
        setExtVid={setEditVid}
        onSelected={upsertVid}
      />
      <ConfirmDialog
        title="Delete External Video?"
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDelete}
      >
        Are you sure you want to delete?
      </ConfirmDialog>
    </>
  );
};

export default ExternalVideos;
